import { applyThemeOverrides, loadThemeOverrides } from './themeRuntime';

export const APPEARANCE_STORAGE_KEY = 'nextcompli.appearance.preferences';
export const APPEARANCE_UPDATED_EVENT = 'nextcompli:appearance-updated';

export type AppearanceThemeMode = 'system' | 'light' | 'dark';
export type AppearanceDensity = 'comfortable' | 'compact';
export type AppearanceReduceMotion = 'system' | 'on' | 'off';
export type AppearanceFontScale = 'sm' | 'md' | 'lg';

export type AppearancePreferences = {
  themeMode: AppearanceThemeMode;
  density: AppearanceDensity;
  reduceMotion: AppearanceReduceMotion;
  fontScale: AppearanceFontScale;
};

export const DEFAULT_APPEARANCE: AppearancePreferences = {
  // Public site + console should open in light unless the user explicitly chooses Dark/System.
  themeMode: 'light',
  density: 'comfortable',
  reduceMotion: 'system',
  fontScale: 'md',
};

const FONT_SCALE_VALUES: Record<AppearanceFontScale, string> = {
  sm: '0.92',
  md: '1',
  lg: '1.12',
};

const isThemeMode = (value: unknown): value is AppearanceThemeMode => (
  value === 'system' || value === 'light' || value === 'dark'
);

const isDensity = (value: unknown): value is AppearanceDensity => (
  value === 'comfortable' || value === 'compact'
);

const isReduceMotion = (value: unknown): value is AppearanceReduceMotion => (
  value === 'system' || value === 'on' || value === 'off'
);

const isFontScale = (value: unknown): value is AppearanceFontScale => (
  value === 'sm' || value === 'md' || value === 'lg'
);

export const normalizeAppearancePreferences = (
  raw: Partial<AppearancePreferences> | null | undefined,
): AppearancePreferences => ({
  themeMode: isThemeMode(raw?.themeMode) ? raw.themeMode : DEFAULT_APPEARANCE.themeMode,
  density: isDensity(raw?.density) ? raw.density : DEFAULT_APPEARANCE.density,
  reduceMotion: isReduceMotion(raw?.reduceMotion) ? raw.reduceMotion : DEFAULT_APPEARANCE.reduceMotion,
  fontScale: isFontScale(raw?.fontScale) ? raw.fontScale : DEFAULT_APPEARANCE.fontScale,
});

export const loadAppearancePreferences = (): AppearancePreferences => {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_APPEARANCE };
  }
  try {
    const raw = window.localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (!raw) {
      return { ...DEFAULT_APPEARANCE };
    }
    return normalizeAppearancePreferences(JSON.parse(raw) as Partial<AppearancePreferences>);
  } catch {
    return { ...DEFAULT_APPEARANCE };
  }
};

export const resolveThemeMode = (
  themeMode: AppearanceThemeMode,
  prefersDark = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches,
): 'light' | 'dark' => {
  if (themeMode === 'light' || themeMode === 'dark') {
    return themeMode;
  }
  return prefersDark ? 'dark' : 'light';
};

export const resolveReduceMotion = (
  reduceMotion: AppearanceReduceMotion,
  prefersReduced = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
): boolean => {
  if (reduceMotion === 'on') return true;
  if (reduceMotion === 'off') return false;
  return prefersReduced;
};

/* Material/GitHub-style dark tokens: near-black canvas, lighter elevated
 * surfaces, off-white text (not #fff), desaturated brand accents. */
const DARK_COLOR_TOKENS: Record<string, string> = {
  '--color-canvas': '#0d1117',
  '--color-canvas-alt': '#11161d',
  '--color-surface': '#1c2330',
  '--color-surface-muted': '#161b24',
  '--color-border': '#484f58',
  '--color-border-strong': '#6e7681',
  '--color-primary': '#79b8ff',
  '--color-primary-strong': '#a5d0ff',
  '--color-primary-soft': '#1a2f4d',
  '--color-accent': '#a78bfa',
  '--color-success': '#3fb950',
  '--color-danger': '#f85149',
  '--color-text-primary': '#f0f6fc',
  '--color-text-strong': '#ffffff',
  '--color-text-muted': '#c9d1d9',
  '--color-on-primary': '#0d1117',
  '--color-control-bg': '#0d1117',
  '--shadow-soft': '0 8px 24px rgba(1, 4, 9, 0.55)',
};

const applyResolvedColorTokens = (resolvedTheme: 'light' | 'dark'): void => {
  if (typeof document === 'undefined') {
    return;
  }
  // Brand overrides from Theme Designer are applied as inline styles and would
  // otherwise win over [data-theme="dark"] CSS. Re-apply the active mode's colors.
  if (resolvedTheme === 'dark') {
    Object.entries(DARK_COLOR_TOKENS).forEach(([token, value]) => {
      document.documentElement.style.setProperty(token, value);
    });
    return;
  }
  // Drop any prior dark inline tokens so light Theme Designer values can apply.
  Object.keys(DARK_COLOR_TOKENS).forEach((token) => {
    document.documentElement.style.removeProperty(token);
  });
  applyThemeOverrides(loadThemeOverrides());
};

export const applyAppearancePreferences = (preferences: AppearancePreferences): void => {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  const resolvedTheme = resolveThemeMode(preferences.themeMode);
  const reduceMotionActive = resolveReduceMotion(preferences.reduceMotion);

  root.setAttribute('data-theme', resolvedTheme);
  root.setAttribute('data-theme-mode', preferences.themeMode);
  root.setAttribute('data-density', preferences.density);
  root.setAttribute('data-reduce-motion', reduceMotionActive ? 'true' : 'false');
  root.setAttribute('data-font-scale', preferences.fontScale);
  root.style.setProperty('--font-size-scale', FONT_SCALE_VALUES[preferences.fontScale]);
  root.style.colorScheme = resolvedTheme;
  applyResolvedColorTokens(resolvedTheme);
};

export const saveAppearancePreferences = (preferences: AppearancePreferences): AppearancePreferences => {
  const normalized = normalizeAppearancePreferences(preferences);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(APPEARANCE_STORAGE_KEY, JSON.stringify(normalized));
  }
  applyAppearancePreferences(normalized);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(APPEARANCE_UPDATED_EVENT, { detail: normalized }));
  }
  return normalized;
};

let mediaCleanup: (() => void) | null = null;

export const initializeAppearance = (): AppearancePreferences => {
  const preferences = loadAppearancePreferences();
  applyAppearancePreferences(preferences);

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return preferences;
  }

  if (mediaCleanup) {
    mediaCleanup();
    mediaCleanup = null;
  }

  const colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const syncFromSystem = () => {
    const current = loadAppearancePreferences();
    if (current.themeMode === 'system' || current.reduceMotion === 'system') {
      applyAppearancePreferences(current);
    }
  };

  const onColorSchemeChange = () => syncFromSystem();
  const onMotionChange = () => syncFromSystem();
  const onBrandThemeUpdated = () => {
    applyAppearancePreferences(loadAppearancePreferences());
  };

  const cleanups: Array<() => void> = [];
  if (typeof colorSchemeQuery.addEventListener === 'function') {
    colorSchemeQuery.addEventListener('change', onColorSchemeChange);
    motionQuery.addEventListener('change', onMotionChange);
    cleanups.push(() => {
      colorSchemeQuery.removeEventListener('change', onColorSchemeChange);
      motionQuery.removeEventListener('change', onMotionChange);
    });
  } else if (typeof colorSchemeQuery.addListener === 'function') {
    colorSchemeQuery.addListener(onColorSchemeChange);
    motionQuery.addListener(onMotionChange);
    cleanups.push(() => {
      colorSchemeQuery.removeListener(onColorSchemeChange);
      motionQuery.removeListener(onMotionChange);
    });
  }

  window.addEventListener('nextcompli:theme-updated', onBrandThemeUpdated as EventListener);
  cleanups.push(() => {
    window.removeEventListener('nextcompli:theme-updated', onBrandThemeUpdated as EventListener);
  });
  mediaCleanup = () => {
    cleanups.forEach((cleanup) => cleanup());
  };

  return preferences;
};
